import { useCallback, useEffect, useRef, useState } from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { useOrganization } from "hooks/useOrganization";
import type {
  InsightsResponse,
  ExperienceInsight,
  InsightsGenerationStatus,
  InsightTask,
  InsightTaskState,
  InsightTaskUpdate,
} from "../types";
import { getInsightsBaseUrl } from "../helpers/insights";

export type UseInsightsResult = {
  insights: ExperienceInsight[];
  status: InsightsGenerationStatus | null;
  generatedAt: string | null;
  isLoading: boolean;
  isRefreshing: boolean;
  refresh: () => void;
  updateTaskState: (insightId: string, state: InsightTaskState) => void;
};

export function useInsights(
  organizationId: string,
  workspaceId?: string,
  projectId?: string
): UseInsightsResult {
  const hasAutoTriggered = useRef(false);
  const baseUrl = getInsightsBaseUrl(organizationId, projectId);

  const { organizationPreferences, preferencesLoading } = useOrganization({
    id: organizationId,
  });

  const { data, isLoading, refetch } = useCustom<InsightsResponse>({
    url: `${baseUrl}/latest`,
    method: "get",
    config: {
      query: workspaceId ? { workspace_id: workspaceId } : undefined,
    },
    queryOptions: {
      enabled: Boolean(organizationId),
      staleTime: 0,
      retry: false,
      refetchInterval: (data) => {
        const status = (data as { data: InsightsResponse } | undefined)?.data
          .status;
        return status === "New" || status === "Processing" ? 5000 : false;
      },
    },
    errorNotification: false,
  });

  const { mutate: generateInsights, isPending } =
    useCustomMutation<InsightsResponse>();
  const { mutate: patchTask } = useCustomMutation<InsightTask>();
  const [isRefetchPending, setIsRefetchPending] = useState(false);
  const isRefreshing = isPending || isRefetchPending;
  // Stays false until a generation request has fully settled (success or error)
  const [hasGenerationSettled, setHasGenerationSettled] = useState(false);

  const refresh = useCallback(() => {
    if (!organizationId) return;
    generateInsights(
      {
        url: baseUrl,
        method: "post",
        values: {
          force: false,
          language: organizationPreferences?.language,
        },
        errorNotification: false,
        successNotification: false,
      },
      {
        onSuccess: () => {
          setIsRefetchPending(true);
          refetch()
            .then(() => {
              setIsRefetchPending(false);
              setHasGenerationSettled(true);
            })
            .catch((error) => {
              console.error("Error refetching insights", error);
              setIsRefetchPending(false);
              setHasGenerationSettled(true);
            });
        },
        onError: (error) => {
          setHasGenerationSettled(true);
          // 429 = report is still fresh — silently ignore
          if (error.statusCode === 429) return;
        },
      }
    );
  }, [
    organizationId,
    baseUrl,
    organizationPreferences?.language,
    generateInsights,
    refetch,
  ]);

  const updateTaskState = (insightId: string, state: InsightTaskState) => {
    if (!organizationId || !insightId) return;
    patchTask(
      {
        url: `${baseUrl}/${insightId}/task`,
        method: "patch",
        values: { state } satisfies InsightTaskUpdate,
        errorNotification: false,
        successNotification: false,
      },
      {
        onSuccess: () => {
          refetch().catch((error) => {
            console.error("Error updating task state", error);
          });
        },
      }
    );
  };

  // Auto-generate on first load if no report exists yet
  useEffect(() => {
    if (
      !isLoading &&
      !preferencesLoading &&
      data?.data.report == null &&
      !hasAutoTriggered.current
    ) {
      hasAutoTriggered.current = true;
      refresh();
    }
  }, [isLoading, preferencesLoading, data?.data.report, refresh]);

  const report = data?.data.report;
  const status = data?.data.status ?? null;
  // BE answers "Done" with no report before the first generation; that isn't an empty result yet
  const isAwaitingFirstReport = report == null && !hasGenerationSettled;

  return {
    insights: report?.insights ?? [],
    status,
    generatedAt: report?.generated_at ?? null,
    isLoading: isLoading || isAwaitingFirstReport,
    isRefreshing,
    refresh,
    updateTaskState,
  };
}
